import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from 'services/api';

function saApi() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('gss_sa_token')}` } };
}

/* ⚠️ `annulee` MANQUAIT DANS LES DEUX TABLES — mesure du 18/09/2026. Une piece
 *    annulee s'affichait donc avec un libelle VIDE et sans couleur : la colonne
 *    Statut ne montrait RIEN. Encore un blanc indiscernable d'une panne, dans
 *    l'ecran meme ou le bouton muet vient d'etre corrige. Un etat qui existe en
 *    base et qu'aucun ecran ne sait nommer est un etat que personne ne verra. */
const STATUT_COLORS = { payee: 'var(--gss-success)', en_attente: 'var(--gss-warning)', retard: 'var(--gss-danger)', annulee: 'var(--gss-text-light)' };
const STATUT_LABELS = { payee: 'Payee', en_attente: 'En attente', retard: 'En retard', annulee: 'Annulee' };

/* ═══ OU LE BOUTON « ANNULER » A LE DROIT D'APPARAITRE ═══════════════════════
 * ⚠️ IL SUIT LA ROUTE, IL NE LA DEVANCE PAS. Pas de bouton grise, pas de bouton
 *    qui rendrait 409 : sur une piece deja annulee, il n'y a PAS de bouton.
 * ⛔ ET PAS SUR UNE PIECE PAYEE. La route, elle, l'accepterait — mesure du
 *    18/09 : elle ne refuse que `annulee`. Mais **annuler un encaissement est
 *    un acte comptable, pas un bouton** (Dom). Tant que cette decision n'est
 *    pas prise, l'ecran n'offre pas le clic. *Le serveur n'a pas ete touche :
 *    c'est l'ECRAN qui s'abstient, et il le dit a la ligne concernee.* */
const ANNULABLE_A_L_ECRAN = ['en_attente', 'retard'];

export default function SuperAdminFacturation() {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // ⚠️ RAPPORT DE FIN DE LOT, DANS LA PAGE. L'`alert()` precedent n'annoncait
  //    que `created` : un lot qui passait pour 19 tenants sur 20 ne disait PAS
  //    lequel avait echoue, ni pourquoi. Un compte sans son contenu n'informe
  //    de rien. (Et une modale bloque toute verification automatisee.)
  const [rapport, setRapport] = useState(null);

  const load = () => {
    api.get('/super-admin/factures', saApi()).then(r => setFactures(r.data))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const generer = async () => {
    setGenerating(true);
    setRapport(null);
    try {
      const r = await api.post('/super-admin/factures/generer', {}, saApi());
      setRapport({ ok: true, ...r.data });
      load();
    } catch (err) {
      setRapport({ ok: false, message: err.response?.data?.error || 'Erreur lors de la generation' });
    } finally { setGenerating(false); }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get('/super-admin/factures/export', { ...saApi(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'factures-saas.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Erreur export'); }
  };

  const marquerPayee = async (id) => {
    await api.patch(`/super-admin/factures/${id}`, { statut: 'payee', mode_paiement: 'virement' }, saApi());
    load();
  };

  /* ═══ ANNULATION — ON LIT CE QU'ON ANNULE AVANT DE LE CONFIRMER ═══════════
   * ⚠️ PAS UN CLIC SIMPLE SUR UNE LIGNE DE TABLEAU. La personne voit le numero,
   *    la societe, la periode et le montant avant de confirmer : ce sont les
   *    quatre elements qui distinguent une piece d'une autre dans une liste ou
   *    toutes se ressemblent.
   * ⛔ LE MOTIF NE SE PRE-REMPLIT PAS, et il n'y a PAS de liste deroulante avec
   *    un choix par defaut. **Un motif par defaut est un motif que personne n'a
   *    ecrit** — et c'est celui-la qu'on relira dans un an sans rien comprendre.
   *    Le bouton de confirmation reste INERTE tant que le motif est vide. */
  const [aAnnuler, setAAnnuler] = useState(null);   // la piece en cours d'annulation
  const [motifAnnul, setMotifAnnul] = useState('');
  const [annulEnCours, setAnnulEnCours] = useState(false);
  const [messageAnnul, setMessageAnnul] = useState(null);

  const ouvrirAnnulation = (f) => {
    setMessageAnnul(null);
    setMotifAnnul('');            // jamais de report du motif precedent
    setAAnnuler(f);
  };

  const confirmerAnnulation = async () => {
    if (!aAnnuler || !motifAnnul.trim()) return;
    setAnnulEnCours(true);
    try {
      const r = await api.post(`/super-admin/factures/${aAnnuler.id}/annuler`,
        { motif: motifAnnul.trim() }, saApi());
      /* ⚠️ L'ECRAN DIT CE QUI S'EST PASSE. Jamais un blanc apres l'acte : un
       *    blanc est indiscernable d'une panne, on vient d'en payer le prix sur
       *    le bouton de generation. Et le numero est REPETE — il est conserve,
       *    c'est ce qui rend l'annulation lisible dans la sequence. */
      setMessageAnnul({ ok: true,
        texte: `Facture ${r.data.numero} annulee. Elle garde son numero et reste `
          + 'consultable, avec son motif, son auteur et sa date.' });
      setAAnnuler(null);
      setMotifAnnul('');
      load();
    } catch (err) {
      setMessageAnnul({ ok: false,
        texte: err.response?.data?.error || "L'annulation n'a pas abouti." });
    } finally { setAnnulEnCours(false); }
  };

  const downloadPdf = async (id, numero) => {
    try {
      const r = await api.get(`/super-admin/factures/${id}/pdf`, { ...saApi(), responseType: 'arraybuffer' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Erreur generation PDF'); }
  };

  // ═════════════════════════════════════════════════════════════════════════
  // TOTAUX — DEUX FILTRES, CHACUN AVEC SON MOTIF
  // ═════════════════════════════════════════════════════════════════════════
  // ⚠️ DEFAUT MESURE LE 11/09/2026, EN PRODUCTION. Ces trois sommes portaient
  //    sur `factures` ENTIER, sans aucun filtre. L'unique facture de la base —
  //    SAAS-202606-001, 205 000 F, FOX, statut `annulee` — les alimentait
  //    toutes les trois : l'ecran affichait 205 000 / 0 / 205 000.
  //    Le correctif du 02/09 n'avait touche que le BACKEND du tableau de bord ;
  //    cet ecran refaisait ses propres totaux cote client et n'a jamais suivi.
  //    Deux chemins pour une notion : le serveur excluait, le navigateur
  //    re-incluait.
  //
  // ① UNE FACTURE ANNULEE SORT DES CALCULS, JAMAIS DES DONNEES (regle 39).
  //    Elle reste dans le TABLEAU ci-dessous, avec son statut et son motif :
  //    c'est une piece, et la trace d'une decision. Elle n'est ni un du, ni un
  //    revenu, ni un oubli.
  // ② UN IMPAYE DE SOI-MEME ENVERS SOI-MEME N'EST PAS UN IMPAYE
  //    (decision de Dom, 11/09/2026). FOX et GLADIATOR sont `tenant_interne`,
  //    VIGIE est `est_demonstration` : leurs factures sont INTRAGROUPE. Elles
  //    ne sont pas cachees — elles sont comptees A PART et DITES sous le
  //    chiffre, exactement comme le fait deja la tuile du tableau de bord.
  const estAnnulee = (f) => f.statut === 'annulee';
  const estIntragroupe = (f) => Boolean(f.tenant_interne) || Boolean(f.est_demonstration);

  const retenues = factures.filter(f => !estAnnulee(f) && !estIntragroupe(f));
  const totalTTC = retenues.reduce((s, f) => s + Number(f.total_ttc || 0), 0);
  const totalPaye = retenues.filter(f => f.statut === 'payee').reduce((s, f) => s + Number(f.total_ttc || 0), 0);
  const totalImpaye = totalTTC - totalPaye;

  // Ce qui a ete mis de cote, pour que l'ecran puisse le DIRE.
  const intragroupe = factures.filter(f => !estAnnulee(f) && estIntragroupe(f));
  const totalIntragroupe = intragroupe.reduce((s, f) => s + Number(f.total_ttc || 0), 0);
  const nbAnnulees = factures.filter(estAnnulee).length;

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Link to="/super-admin" style={{ color: 'var(--gss-text-light)', fontSize: 12, textDecoration: 'none' }}>Dashboard</Link>
          {' · '}
          <Link to="/super-admin/facturation/historique" style={{ color: 'var(--gss-text-light)', fontSize: 12, textDecoration: 'none' }}>Historique</Link>
          <span style={{ color: 'var(--gss-text-light)', margin: '0 8px' }}>/</span>
          <span style={{ color: 'var(--gss-text)', fontSize: 14, fontWeight: 700 }}>Facturation SaaS</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={exportExcel} style={{ ...S.btn, background: 'transparent', border: '1px solid var(--gss-border-strong)', color: 'var(--gss-text-light)' }}>Export Excel</button>
          <button onClick={generer} disabled={generating} style={S.btn}>
            {generating ? 'Generation...' : 'Generer factures du mois'}
          </button>
        </div>
      </div>

      {/* ═══ RAPPORT DE FIN DE LOT ═══
          ⚠️ « 19 sur 20 » sans dire LEQUEL a echoue n'ouvre aucune action.
             Le rapport nomme chaque client en echec et sa raison, en clair. */}
      {/* ⚠️ « RIEN A EMETTRE » N'EST PAS UN SUCCES, ET CE N'EST PAS UNE PANNE.
             Defaut mesure le 18/09/2026 : la route repondait 200 avec
             « 0 facture emise pour 08/2026. » — affiche ICI, EN VERT. Une phrase
             exacte, creuse, en couleur de succes : Dom y a lu « rien ne se
             passe, le systeme ne dit rien ». Le bouton n'etait pas muet, il
             chuchotait. Un lot qui n'emet rien s'affiche donc en NEUTRE, et il
             doit nommer ce qu'il a ecarte, juste en dessous. */}
      {rapport && (
        <div data-testid="rapport-facturation" style={{
          border: `1px solid ${rapport.ok === false ? 'var(--gss-danger)'
            : (rapport.echecs?.length ? 'var(--gss-warning)'
            : (rapport.created === 0 ? 'var(--gss-text-light)' : 'var(--gss-success)'))}`,
          borderRadius: 10, padding: 16, marginBottom: 24,
          background: 'var(--gss-card, rgba(255,255,255,0.03))',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-text)' }}>
              {rapport.ok === false ? 'La generation a echoue' : rapport.rapport}
            </div>
            <button onClick={() => setRapport(null)} style={{
              background: 'transparent', border: 'none', color: 'var(--gss-text-light)',
              cursor: 'pointer', fontSize: 16, lineHeight: 1,
            }}>&times;</button>
          </div>

          {rapport.ok === false && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--gss-danger)' }}>{rapport.message}</div>
          )}

          {/* ═══ CE QUI A ETE ECARTE, ET POURQUOI ═══
              ⚠️ LE BLOC QUI MANQUAIT. Sans lui, trois abonnements etaient lus,
                 tous les trois ecartes a bon droit, et AUCUN n'etait nomme :
                 l'ecran ne montrait qu'un « 0 » vert. Chaque exclu porte son
                 motif, en clair. On ne resume jamais en « et N autres » : un
                 exclu non nomme est un exclu que personne ne verifiera.
              ⓘ FOX et GLADIATOR appartiennent au groupe de l'editeur : les voir
                ici, exclus et nommes, est le comportement ATTENDU — pas un
                defaut a corriger. Le 02/09, faute de ce filtre, FOX portait une
                facture de 205 000 F emise a elle-meme. */}
          {rapport.ok !== false && rapport.exclus?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gss-text-secondary)' }}>
              <strong>Ecartes de la facturation</strong>{' '}
              ({rapport.exclus.length} sur {rapport.examines} abonnement{rapport.examines > 1 ? 's' : ''} examine{rapport.examines > 1 ? 's' : ''}) :
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {rapport.exclus.map((e) => (
                  <li key={e.tenant}>{e.tenant} — {e.motif}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Aucun retenu, aucun exclu : il n'y a tout simplement aucun
              abonnement en base. Le dire, plutot que d'afficher un 0 seul. */}
          {rapport.ok !== false && rapport.examines === 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gss-text-secondary)' }}>
              Aucun abonnement n'est enregistre : il n'y a rien a facturer pour cette periode.
            </div>
          )}

          {/* Clients ignores : deja factures pour la periode. Ce n'est pas une
              erreur — c'est l'idempotence qui fait son travail. */}
          {rapport.dejaFactures?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gss-text-secondary)' }}>
              <strong>Deja factures pour cette periode</strong> (aucune facture en double) :{' '}
              {rapport.dejaFactures.join(', ')}
            </div>
          )}

          {rapport.echecs?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gss-warning)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                Clients en echec — les autres factures ont bien ete emises
              </div>
              {/* ⚠️ `width: 100%` NE SUFFIT PAS quand une cellule porte
                  `whiteSpace: nowrap` : le nom de société impose une largeur
                  minimale et pousse la page. Le tableau scrolle donc DANS SON
                  PROPRE conteneur, jamais en déplaçant la page. */}
              <div className="gss-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {rapport.echecs.map((e, i) => (
                    <tr key={i}>
                      <td style={{ padding: '4px 8px 4px 0', color: 'var(--gss-text)', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{e.tenant}</td>
                      <td style={{ padding: '4px 0', color: 'var(--gss-text-secondary)' }}>{e.raison}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Totaux */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 16, marginBottom: 24 }}>
        {/* ⚠️ TOUTE TUILE PORTE LE PERIMETRE QU'ELLE COMPTE (regle 39). Sans ce
            sous-titre, un zero et un « rien a compter » sont indiscernables. */}
        <div style={S.card}>
          <div style={{ fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Total facture — clients</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--gss-text)' }}>{totalTTC.toLocaleString()} F</div>
          <div style={{ fontSize: 10, color: 'var(--gss-text-light)', marginTop: 4 }}>
            Hors intragroupe et hors factures annulees
          </div>
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Paye</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--gss-success)' }}>{totalPaye.toLocaleString()} F</div>
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Impaye</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--gss-danger)' }}>{totalImpaye.toLocaleString()} F</div>
          {/* ⚠️ MIS DE COTE, PAS CACHE. Ce qui sort du calcul se DIT sous le
              chiffre : un ecart qu'on ne voit pas est un ecart qu'on rediscute
              dans six mois. */}
          {(totalIntragroupe > 0 || nbAnnulees > 0) && (
            <div style={{ fontSize: 10, color: 'var(--gss-text-light)', marginTop: 4, lineHeight: 1.5 }}>
              {totalIntragroupe > 0 && (
                <div>dont <strong>{totalIntragroupe.toLocaleString()} F intragroupe</strong>, hors de ce total — facture de l&apos;editeur a lui-meme</div>
              )}
              {nbAnnulees > 0 && (
                <div>{nbAnnulees} facture{nbAnnulees > 1 ? 's' : ''} annulee{nbAnnulees > 1 ? 's' : ''}, hors calcul — visible{nbAnnulees > 1 ? 's' : ''} dans le tableau</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ MESSAGE D'ANNULATION — L'ECRAN NE RESTE JAMAIS MUET ═══ */}
      {messageAnnul && (
        <div data-testid="message-annulation" style={{
          border: `1px solid ${messageAnnul.ok ? 'var(--gss-success)' : 'var(--gss-danger)'}`,
          borderRadius: 10, padding: 14, marginBottom: 20,
          background: 'var(--gss-card, rgba(255,255,255,0.03))',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gss-text)' }}>{messageAnnul.texte}</div>
          <button onClick={() => setMessageAnnul(null)} style={{
            background: 'transparent', border: 'none', color: 'var(--gss-text-light)',
            cursor: 'pointer', fontSize: 16, lineHeight: 1,
          }}>&times;</button>
        </div>
      )}

      {/* ═══ CONFIRMATION D'ANNULATION — ON LIT LA PIECE, PUIS ON ECRIT LE MOTIF ═══ */}
      {aAnnuler && (
        <div data-testid="confirmation-annulation" style={{
          border: '1px solid var(--gss-danger)', borderRadius: 10, padding: 18, marginBottom: 20,
          background: 'var(--gss-card, rgba(255,255,255,0.03))',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 10 }}>
            Annuler cette facture
          </div>

          {/* Les quatre elements qui identifient la piece, avant toute confirmation. */}
          <div style={{ fontSize: 12.5, color: 'var(--gss-text-secondary)', lineHeight: 1.9, marginBottom: 12 }}>
            <div><strong>Numero</strong> : {aAnnuler.numero}</div>
            <div><strong>Societe</strong> : {aAnnuler.tenant_nom}</div>
            <div><strong>Periode</strong> : {aAnnuler.mois}/{aAnnuler.annee}</div>
            <div><strong>Montant TTC</strong> : {Number(aAnnuler.total_ttc).toLocaleString('fr-FR')} F</div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--gss-text-light)', marginBottom: 10 }}>
            La piece n'est ni modifiee ni supprimee : elle passe a l'etat annule et
            <strong> garde son numero</strong>. Le motif est conserve avec elle.
          </div>

          <textarea
            data-testid="motif-annulation"
            value={motifAnnul}
            onChange={(e) => setMotifAnnul(e.target.value)}
            placeholder="Motif de l'annulation (obligatoire)"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8,
              border: '1px solid var(--gss-border)', background: 'var(--gss-white)',
              color: 'var(--gss-text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
            }} />

          <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
            {/* ⛔ INERTE TANT QUE LE MOTIF EST VIDE — le refus serveur existe, mais
                   on ne fait pas cliquer quelqu'un dans le vide pour le lui apprendre. */}
            <button
              data-testid="confirmer-annulation"
              disabled={!motifAnnul.trim() || annulEnCours}
              onClick={confirmerAnnulation}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13,
                background: motifAnnul.trim() ? 'var(--gss-danger)' : 'var(--gss-border)',
                color: motifAnnul.trim() ? '#FFF' : 'var(--gss-text-light)',
                cursor: motifAnnul.trim() && !annulEnCours ? 'pointer' : 'not-allowed',
              }}>
              {annulEnCours ? 'Annulation...' : "Confirmer l'annulation"}
            </button>
            <button onClick={() => { setAAnnuler(null); setMotifAnnul(''); }} style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid var(--gss-border)',
              background: 'transparent', color: 'var(--gss-text-secondary)', fontSize: 13, cursor: 'pointer',
            }}>Renoncer</button>
            {!motifAnnul.trim() && (
              <span style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>
                Le motif est obligatoire.
              </span>
            )}
          </div>
        </div>
      )}

      <div style={S.card}>
        <div className="gss-table-wrap">
        <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['N\u00B0','Societe','Mois','Agents','Agents (F)','Modules (F)','Remise','Total TTC','Statut','Actions'].map(h =>
              <th key={h} style={S.th}>{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {factures.map(f => (
              <tr key={f.id}>
                {/* ⚠️ LE NUMERO EST CONSERVE APRES ANNULATION, et le motif s'affiche
                    sous lui. Une piece annulee dont on ne lit pas le motif est une
                    piece qu'on ne peut plus expliquer un an apres — c'est
                    exactement ce que l'obligation de motif existe pour empecher. */}
                <td style={{ ...S.td, color: 'var(--gss-text-light)', fontSize: 11 }}>
                  {f.numero}
                  {f.statut === 'annulee' && f.motif_annulation && (
                    <div style={{ marginTop: 3, fontSize: 10.5, fontStyle: 'italic', maxWidth: 220 }}>
                      {f.motif_annulation}
                    </div>
                  )}
                </td>
                <td style={{ ...S.td, color: 'var(--gss-text)', fontWeight: 600 }}>{f.tenant_nom}</td>
                <td style={{ ...S.td, color: 'var(--gss-text-secondary)' }}>{f.mois}/{f.annee}</td>
                <td style={{ ...S.td, textAlign: 'center' }}>{f.nb_agents_actifs}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{Number(f.sous_total_agents).toLocaleString()}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{Number(f.modules_montant).toLocaleString()}</td>
                {/* ⚠️ « -0 » EN ROUGE N'EST PAS UN MONTANT. Le signe moins etait
                    ECRIT EN DUR devant la valeur : une remise nulle s'affichait
                    donc « -0 », un zero negatif, en couleur d'alerte. Une absence
                    de remise n'est pas une remise de zero franc — elle se dit
                    « — », sans couleur. */}
                <td style={{ ...S.td, textAlign: 'right', color: Number(f.remise_montant) > 0 ? 'var(--gss-danger)' : 'var(--gss-text-light)' }}>
                  {Number(f.remise_montant) > 0 ? `-${Number(f.remise_montant).toLocaleString()}` : '—'}
                </td>
                <td style={{ ...S.td, textAlign: 'right', color: 'var(--gss-red)', fontWeight: 700 }}>{Number(f.total_ttc).toLocaleString()}</td>
                <td style={S.td}>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    color: STATUT_COLORS[f.statut],
                  }}>{STATUT_LABELS[f.statut]}</span>
                </td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => downloadPdf(f.id, f.numero)} style={{
                      padding: '4px 12px', borderRadius: 6, border: '1px solid var(--gss-red)',
                      background: 'transparent', color: 'var(--gss-red)', fontSize: 11, cursor: 'pointer',
                    }}>PDF</button>
                    {/* ⚠️ « Payee » NE S'OFFRE PLUS SUR UNE PIECE ANNULEE : le
                        serveur rend desormais 409 PIECE_ANNULEE_FIGEE, et un
                        bouton qui ne peut que rendre une erreur n'est pas un
                        bouton — c'est un piege. */}
                    {f.statut !== 'payee' && f.statut !== 'annulee' && (
                      <button onClick={() => marquerPayee(f.id)} style={{
                        padding: '4px 12px', borderRadius: 6, border: '1px solid var(--gss-success)',
                        background: 'transparent', color: 'var(--gss-success)', fontSize: 11, cursor: 'pointer',
                      }}>Payee</button>
                    )}
                    {ANNULABLE_A_L_ECRAN.includes(f.statut) && (
                      <button data-testid={`annuler-${f.numero}`} onClick={() => ouvrirAnnulation(f)} style={{
                        padding: '4px 12px', borderRadius: 6, border: '1px solid var(--gss-danger)',
                        background: 'transparent', color: 'var(--gss-danger)', fontSize: 11, cursor: 'pointer',
                      }}>Annuler</button>
                    )}
                    {/* ⚠️ L'ABSENCE DE BOUTON SE DIT, ELLE NE SE DEVINE PAS : sans
                        cette ligne, un encaissement ressemble a une piece qu'on a
                        oublie de rendre annulable.
                        ⓘ CE TEXTE NE FAIT PLUS SEUL LE TRAVAIL. Jusqu'au 18/09 il
                          etait la SEULE chose qui empechait d'annuler une facture
                          encaissee : le serveur, lui, l'acceptait. Un garde qui
                          n'existe qu'a l'ecran n'est pas un garde — il suffisait
                          de passer par l'API. Le refus est desormais SERVEUR
                          (409 FACTURE_ENCAISSEE_AVOIR_REQUIS) ; cette ligne ne
                          fait que l'annoncer avant le clic. */}
                    {f.statut === 'payee' && (
                      <span title={"Une facture encaissee ne s'annule pas : l'annuler laisserait un "
                        + "encaissement sans cause. La correction passe par un avoir — piece nouvelle, "
                        + "de sens inverse, avec son propre numero."}
                        style={{ fontSize: 10.5, color: 'var(--gss-text-light)', alignSelf: 'center' }}>
                        Encaissee — correction par avoir
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {loading && <p style={{ color: 'var(--gss-text-light)', padding: 20 }}>Chargement...</p>}
        {!loading && factures.length === 0 && <p style={{ color: 'var(--gss-text-light)', padding: 20 }}>Aucune facture</p>}
      </div>
    </div>
  );
}

const S = {
  page: { width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: 'var(--gss-bg)', padding: 'clamp(12px, 3vw, 28px)' },
  card: { background: 'var(--gss-white)', borderRadius: 12, padding: 20, border: '1px solid var(--gss-border)', minWidth: 0 },
  btn: { padding: '8px 20px', borderRadius: 8, background: 'var(--gss-red)', color: '#FFF', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid var(--gss-border)' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--gss-border)', fontSize: 12, color: 'var(--gss-text-secondary)' },
};
